
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, AlertTriangle, Clock } from "lucide-react";

interface PaymentStatusDialogProps {
  isOpen: boolean;
  onClose: () => void;
  status: "success" | "failed" | "pending" | null;
  message?: string;
  error?: any;
}

const PaymentStatusDialog = ({
  isOpen,
  onClose,
  status,
  message,
  error
}: PaymentStatusDialogProps) => {
  // Auto-close after 15 seconds for success, but not for failed or pending
  useEffect(() => {
    if (isOpen && status === "success") {
      const timer = setTimeout(() => {
        onClose();
      }, 15000);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose, status]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="flex flex-col items-center text-center">
          <div className="mb-4">
            {status === "success" ? (
              <CheckCircle className="h-16 w-16 text-green-500" />
            ) : status === "failed" ? (
              <XCircle className="h-16 w-16 text-red-500" />
            ) : status === "pending" ? (
              <Clock className="h-16 w-16 text-yellow-500" />
            ) : (
              <AlertTriangle className="h-16 w-16 text-yellow-500" />
            )}
          </div>
          <DialogTitle className="text-xl">
            {status === "success" 
              ? "Payment Successful!" 
              : status === "failed"
              ? "Payment Failed"
              : status === "pending"
              ? "Payment is Pending"
              : "Payment Status Check"}
          </DialogTitle>
          <div className="text-center mt-4">
            <p>
              {message || (status === "success" 
                ? "Your payment has been successfully processed." 
                : status === "failed"
                ? "There was a problem processing your payment."
                : status === "pending"
                ? "Your payment is still being processed. This might take a few minutes."
                : "We're checking your payment status.")}
            </p>
            
            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                <p className="font-medium">Error details:</p>
                <p className="mt-1">{error.message || JSON.stringify(error)}</p>
                {error.details && (
                  <p className="mt-1 text-xs">{error.details}</p>
                )}
              </div>
            )}
            
            {status === "pending" && (
              <div className="mt-4 text-sm text-muted-foreground">
                <p>This could happen because:</p>
                <ul className="list-disc text-left ml-8 mt-2">
                  <li>The payment process is not yet complete</li>
                  <li>Your bank is still processing the transaction</li>
                  <li>There's a delay in the payment provider's system</li>
                </ul>
                <p className="mt-2">You can check back later or contact support if it persists.</p>
              </div>
            )}
          </div>
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button onClick={onClose}>
            {status === "success" ? "Continue" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentStatusDialog;
